import React, { useState, useEffect } from 'react';
import { auditsApi } from '../services/api';
import api from '../services/api';

interface AuditInfo {
  id: number;
  titulo: string;
  cliente: string;
  operador: string;
  estado: string;
  fechaCreacion: string;
  fechaTerminacion: string | null;
  costoFinal: number;
  tiempoEstimadoMinutos?: number;
  horasEstimadas?: number;
  archivosCount: number;
  antiguedad: number; // días desde creación
  motivoPausa?: string;
}

const AuditManagement: React.FC = () => {
  const [audits, setAudits] = useState<AuditInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filter, setFilter] = useState('');
  const [ageFilter, setAgeFilter] = useState<'all' | 'old' | 'recent'>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [dateFilter, setDateFilter] = useState<'all' | 'today' | 'yesterday' | 'this_week' | 'this_month' | 'last_month' | 'specific_day'>('all');
  const [specificDate, setSpecificDate] = useState('');
  const [deleting, setDeleting] = useState<number | null>(null);
  const [showConfirm, setShowConfirm] = useState<{auditId: number, type: 'cleanup' | 'correction'} | null>(null);
  const [operadores, setOperadores] = useState<any[]>([]);
  const [showReassignModal, setShowReassignModal] = useState<{audit: AuditInfo} | null>(null);
  const [selectedOperator, setSelectedOperator] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    await Promise.all([loadAudits(), loadOperators()]);
  };

  const loadOperators = async () => {
    try {
      const response = await api.get('/users/assignable');
      if (response.data.success) {
        setOperadores(response.data.data);
      }
    } catch (err: any) {
      console.error('Error cargando usuarios asignables:', err);
    }
  };

  const loadAudits = async () => {
    try {
      setLoading(true);
      const response = await auditsApi.getAllForManagement();
      if (response.data.success) {
        setAudits(response.data.data);
      }
    } catch (err: any) {
      setError('Error cargando auditorías: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const deleteAudit = async (auditId: number, type: 'cleanup' | 'correction') => {
    try {
      setDeleting(auditId);
      const response = await auditsApi.deleteAudit(auditId, type);
      if (response.data.success) {
        const actionText = type === 'cleanup' ? 'eliminada (limpieza)' : 'eliminada y revertidos los pagos/cobros';
        setSuccess(`Auditoría #${auditId} ${actionText} exitosamente`);
        await loadAudits();
      }
    } catch (err: any) {
      setError('Error eliminando auditoría: ' + (err.response?.data?.error || err.message));
    } finally {
      setDeleting(null);
      setShowConfirm(null);
    }
  };

  const handleDeleteClick = (audit: AuditInfo) => {
    const isOld = audit.antiguedad > 30;
    const type = isOld ? 'cleanup' : 'correction';
    setShowConfirm({ auditId: audit.id, type });
  };

  const confirmDelete = () => {
    if (showConfirm) {
      deleteAudit(showConfirm.auditId, showConfirm.type);
    }
  };

  const reasignarAuditoria = async (auditId: number, newOperatorId: string) => {
    try {
      const response = await api.put(`/audits/${auditId}/reassign`, {
        operadorId: parseInt(newOperatorId)
      });
      if (response.data.success) {
        setSuccess(`Auditoría #${auditId} reasignada exitosamente`);
        setShowReassignModal(null);
        setSelectedOperator('');
        await loadAudits();
      }
    } catch (err: any) {
      setError('Error reasignando auditoría: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleReassignClick = (audit: AuditInfo) => {
    setShowReassignModal({ audit });
    setSelectedOperator('');
  };

  const handleReassignConfirm = () => {
    if (showReassignModal && selectedOperator) {
      reasignarAuditoria(showReassignModal.audit.id, selectedOperator);
    }
  };

  const devolverACorreccion = async (auditId: number) => {
    if (!confirm('⚠️ ¿Está seguro de devolver esta auditoría para corrección?\n\n- Cambiará de "entregada" a "terminada"\n- Revertirá el cobro (pendiente)\n- El cliente NO la verá\n- El operador/supervisor podrá editarla')) {
      return;
    }

    try {
      await api.patch(`/audits/${auditId}/devolver-correccion`);
      setSuccess('✅ Auditoría devuelta para corrección');
      loadAudits();
    } catch (err: any) {
      setError('❌ Error devolviendo a corrección: ' + (err.response?.data?.error || err.message));
    }
  };

  const openHtmlReport = async (auditId: number) => {
    try {
      const htmlContent = await auditsApi.getHTMLReport(auditId);
      const blob = new Blob([htmlContent], { type: 'text/html' });
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
    } catch (error) {
      console.error('Error abriendo HTML:', error);
      alert('Error al abrir el reporte HTML');
    }
  };

  const downloadPdfReport = async (auditId: number) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert('No estás autenticado. Por favor, inicia sesión nuevamente.');
        return;
      }
      await auditsApi.downloadPDF(auditId);
    } catch (error) {
      console.error('Error descargando reporte PDF:', error);
      alert('Error al descargar el reporte PDF');
    }
  };

  const getStatusColor = (estado: string) => {
    switch (estado) {
      case 'pendiente': return '#ffc107';
      case 'asignada': return '#17a2b8';
      case 'en_proceso': return '#28a745';
      case 'pausada': return '#dc2626';
      case 'terminada': return '#007bff';
      case 'entregada': return '#6c757d';
      default: return '#6c757d';
    }
  };

  const getStatusIcon = (estado: string) => {
    switch (estado) {
      case 'pendiente': return '⏳';
      case 'asignada': return '📋';
      case 'en_proceso': return '🔄';
      case 'pausada': return '⏸️';
      case 'terminada': return '✅';
      case 'entregada': return '📦';
      default: return '❓';
    }
  };

  const filteredAudits = audits.filter(audit => {
    const filterLower = filter.toLowerCase();
    const matchesText =
      audit.titulo?.toLowerCase().includes(filterLower) ||
      audit.cliente?.toLowerCase().includes(filterLower) ||
      (audit as any).sucursal?.toLowerCase().includes(filterLower) ||
      audit.operador?.toLowerCase().includes(filterLower) ||
      audit.id.toString().includes(filter);

    const matchesAge =
      ageFilter === 'all' ||
      (ageFilter === 'old' && audit.antiguedad > 30) ||
      (ageFilter === 'recent' && audit.antiguedad <= 30);

    const matchesStatus =
      statusFilter === 'all' ||
      audit.estado === statusFilter;

    // Filtro por fecha
    let matchesDate = true;
    if (dateFilter !== 'all') {
      const auditDate = new Date(audit.fechaCreacion);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const auditDateOnly = new Date(auditDate);
      auditDateOnly.setHours(0, 0, 0, 0);

      if (dateFilter === 'today') {
        matchesDate = auditDateOnly.getTime() === today.getTime();
      } else if (dateFilter === 'yesterday') {
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        matchesDate = auditDateOnly.getTime() === yesterday.getTime();
      } else if (dateFilter === 'this_week') {
        const weekStart = new Date(today);
        const dayOfWeek = today.getDay();
        const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Lunes como inicio de semana
        weekStart.setDate(today.getDate() - diff);
        matchesDate = auditDateOnly >= weekStart;
      } else if (dateFilter === 'this_month') {
        matchesDate =
          auditDate.getMonth() === today.getMonth() &&
          auditDate.getFullYear() === today.getFullYear();
      } else if (dateFilter === 'last_month') {
        const lastMonth = new Date(today);
        lastMonth.setMonth(lastMonth.getMonth() - 1);
        matchesDate =
          auditDate.getMonth() === lastMonth.getMonth() &&
          auditDate.getFullYear() === lastMonth.getFullYear();
      } else if (dateFilter === 'specific_day' && specificDate) {
        const selectedDate = new Date(specificDate);
        selectedDate.setHours(0, 0, 0, 0);
        matchesDate = auditDateOnly.getTime() === selectedDate.getTime();
      }
    }

    return matchesText && matchesAge && matchesStatus && matchesDate;
  });

  const oldAudits = audits.filter(a => a.antiguedad > 30);
  const recentAudits = audits.filter(a => a.antiguedad <= 30);

  // Agrupar auditorías por cliente
  const auditoriasPorCliente = filteredAudits.reduce((grupos, audit) => {
    const cliente = audit.cliente || 'Sin cliente';
    if (!grupos[cliente]) {
      grupos[cliente] = {
        cliente,
        auditorias: [],
        fechaMasAntigua: audit.fechaCreacion
      };
    }
    grupos[cliente].auditorias.push(audit);
    return grupos;
  }, {} as Record<string, { cliente: string; auditorias: AuditInfo[]; fechaMasAntigua: string }>);

  // Ordenar grupos por la auditoría más antigua (clientes con auditorías más viejas primero)
  const gruposOrdenados = Object.values(auditoriasPorCliente).sort((a, b) =>
    new Date(a.fechaMasAntigua).getTime() - new Date(b.fechaMasAntigua).getTime()
  );

  const clearMessages = () => {
    setError('');
    setSuccess('');
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div>⏳ Cargando auditorías...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '1.5rem' }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '2rem'
      }}>
        <h2 style={{ margin: 0, color: '#495057' }}>🗂️ Gestión de Auditorías</h2>
        <button
          onClick={loadAudits}
          disabled={loading}
          style={{
            background: '#28a745',
            color: 'white',
            border: 'none',
            padding: '0.5rem 1rem',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          🔄 Actualizar
        </button>
      </div>

      {error && (
        <div style={{
          background: '#f8d7da',
          color: '#721c24',
          padding: '0.75rem',
          borderRadius: '4px',
          marginBottom: '1rem',
          border: '1px solid #f5c6cb',
          cursor: 'pointer'
        }} onClick={clearMessages}>
          ❌ {error} (click para cerrar)
        </div>
      )}

      {success && (
        <div style={{
          background: '#d4edda',
          color: '#155724',
          padding: '0.75rem',
          borderRadius: '4px',
          marginBottom: '1rem',
          border: '1px solid #c3e6cb',
          cursor: 'pointer'
        }} onClick={clearMessages}>
          ✅ {success} (click para cerrar)
        </div>
      )}

      {/* Estadísticas */}
      <div style={{
        background: '#f8f9fa',
        padding: '1rem',
        borderRadius: '6px',
        marginBottom: '1.5rem',
        border: '1px solid #dee2e6'
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
          <div>
            <strong>📊 Total auditorías (filtradas):</strong> {filteredAudits.length}
          </div>
          <div style={{ color: '#6c757d' }}>
            <strong>📋 Total en sistema:</strong> {audits.length}
          </div>
          <div style={{ color: '#dc3545' }}>
            <strong>🗑️ Antiguas (+30 días):</strong> {oldAudits.length}
          </div>
          <div style={{ color: '#28a745' }}>
            <strong>📝 Recientes (-30 días):</strong> {recentAudits.length}
          </div>
        </div>
      </div>

      {/* Información importante */}
      <div style={{
        background: '#fff3cd',
        border: '1px solid #ffeaa7',
        borderRadius: '6px',
        padding: '1rem',
        marginBottom: '1.5rem',
        fontSize: '0.9rem',
        color: '#856404'
      }}>
        <strong>⚠️ Tipos de eliminación:</strong>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1rem', marginTop: '0.5rem' }}>
          <div>
            <strong>🗑️ Limpieza (+30 días):</strong> Solo libera espacio, NO afecta pagos/cobros
          </div>
          <div>
            <strong>❌ Corrección (-30 días):</strong> Revierte pagos a operadores y cobros a clientes
          </div>
        </div>
      </div>

      {/* Controles de filtrado */}
      <div style={{
        display: 'flex',
        gap: '1rem',
        marginBottom: '1.5rem',
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        <div style={{ flex: 1, minWidth: '200px' }}>
          <input
            type="text"
            placeholder="🔍 Buscar por ID, título, cliente u operador..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '1px solid #ced4da',
              borderRadius: '4px',
              fontSize: '0.9rem'
            }}
          />
        </div>
        <div>
          <label style={{ marginRight: '0.5rem', fontWeight: '500' }}>Filtrar por edad:</label>
          <select
            value={ageFilter}
            onChange={(e) => setAgeFilter(e.target.value as 'all' | 'old' | 'recent')}
            style={{
              padding: '0.5rem',
              border: '1px solid #ced4da',
              borderRadius: '4px'
            }}
          >
            <option value="all">📋 Todas</option>
            <option value="old">🗑️ Antiguas (+30 días)</option>
            <option value="recent">📝 Recientes (-30 días)</option>
          </select>
        </div>
        <div>
          <label style={{ marginRight: '0.5rem', fontWeight: '500' }}>Filtrar por estado:</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{
              padding: '0.5rem',
              border: '1px solid #ced4da',
              borderRadius: '4px'
            }}
          >
            <option value="all">📋 Todos</option>
            <option value="pendiente">⏳ Pendiente</option>
            <option value="asignada">📋 Asignada</option>
            <option value="en_proceso">🔄 En Proceso</option>
            <option value="pausada">⏸️ Pausada</option>
            <option value="terminada">✅ Terminada</option>
            <option value="entregada">📦 Entregada</option>
          </select>
        </div>
        <div>
          <label style={{ marginRight: '0.5rem', fontWeight: '500' }}>Filtrar por fecha:</label>
          <select
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value as 'all' | 'today' | 'yesterday' | 'this_week' | 'this_month' | 'last_month' | 'specific_day')}
            style={{
              padding: '0.5rem',
              border: '1px solid #ced4da',
              borderRadius: '4px'
            }}
          >
            <option value="all">📅 Todas las fechas</option>
            <option value="today">📆 Hoy</option>
            <option value="yesterday">📆 Ayer</option>
            <option value="this_week">📅 Esta semana</option>
            <option value="this_month">📅 Este mes</option>
            <option value="last_month">📅 Mes pasado</option>
            <option value="specific_day">📆 Día específico...</option>
          </select>
        </div>
        {dateFilter === 'specific_day' && (
          <div>
            <label style={{ marginRight: '0.5rem', fontWeight: '500' }}>Seleccionar día:</label>
            <input
              type="date"
              value={specificDate}
              onChange={(e) => setSpecificDate(e.target.value)}
              style={{
                padding: '0.5rem',
                border: '1px solid #ced4da',
                borderRadius: '4px',
                fontSize: '0.9rem'
              }}
            />
          </div>
        )}
      </div>

      {/* Lista de auditorías */}
      {filteredAudits.length === 0 ? (
        <div style={{
          background: '#f8f9fa',
          padding: '2rem',
          textAlign: 'center',
          borderRadius: '6px',
          color: '#6c757d'
        }}>
          {filter ? '🔍 No se encontraron auditorías con ese filtro' : '📭 No hay auditorías'}
        </div>
      ) : (
        <div style={{
          background: 'white',
          border: '1px solid #dee2e6',
          borderRadius: '6px',
          overflow: 'hidden'
        }}>
          {/* Header de tabla */}
          <div style={{
            background: '#f8f9fa',
            padding: '1rem',
            borderBottom: '1px solid #dee2e6',
            display: 'grid',
            gridTemplateColumns: '80px 1fr 120px 120px 100px 80px 180px 120px 100px',
            gap: '1rem',
            alignItems: 'center',
            fontWeight: '600',
            fontSize: '0.9rem'
          }}>
            <div>#ID</div>
            <div>📋 Auditoría</div>
            <div>👤 Operador</div>
            <div>📊 Estado</div>
            <div>📅 Edad</div>
            <div>💰 Costo</div>
            <div>📄 Ver Reportes</div>
            <div>🔄 Reasignar</div>
            <div>🗑️ Eliminar</div>
          </div>

          {/* Filas de auditorías */}
          {filteredAudits.map((audit, index) => (
            <div
              key={audit.id}
              style={{
                padding: '1rem',
                borderBottom: index < filteredAudits.length - 1 ? '1px solid #f1f3f4' : 'none',
                display: 'grid',
                gridTemplateColumns: '80px 1fr 120px 120px 100px 80px 180px 120px 100px',
                gap: '1rem',
                alignItems: 'center',
                fontSize: '0.9rem',
                background: audit.antiguedad > 30 ? '#fff5f5' : 'white'
              }}
            >
              <div style={{ fontWeight: '600', color: '#495057' }}>
                #{audit.id}
              </div>
              
              <div>
                <div style={{ fontWeight: '500', marginBottom: '0.25rem' }}>
                  {audit.titulo}
                </div>
                <div style={{ fontSize: '0.8rem', color: '#6c757d' }}>
                  🏢 {audit.cliente}
                </div>
                <div style={{ fontSize: '0.8rem', color: '#6c757d' }}>
                  📎 {audit.archivosCount} archivo(s)
                </div>
                {audit.horasEstimadas && (
                  <div style={{ fontSize: '0.8rem', color: '#6c757d' }}>
                    ⏱️ Estimado: {Math.floor(audit.horasEstimadas)}h {Math.round((audit.horasEstimadas % 1) * 60)}m
                  </div>
                )}
                {audit.estado === 'pausada' && audit.motivoPausa && (
                  <div style={{
                    fontSize: '0.75rem',
                    color: '#dc2626',
                    background: '#fee2e2',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '0.25rem',
                    marginTop: '0.25rem',
                    maxWidth: 'fit-content'
                  }}>
                    ⏸️ Motivo: {audit.motivoPausa}
                  </div>
                )}
              </div>
              
              <div style={{ fontSize: '0.8rem' }}>
                {audit.operador || 'Sin asignar'}
              </div>
              
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.25rem',
                color: getStatusColor(audit.estado)
              }}>
                <span>{getStatusIcon(audit.estado)}</span>
                <span style={{ fontSize: '0.8rem' }}>{audit.estado}</span>
              </div>
              
              <div style={{ 
                color: audit.antiguedad > 30 ? '#dc3545' : '#28a745',
                fontWeight: '500'
              }}>
                {audit.antiguedad}d
              </div>
              
              <div style={{ textAlign: 'right', fontWeight: '500' }}>
                ${audit.costoFinal.toLocaleString()}
              </div>

              <div style={{ display: 'flex', gap: '0.5rem' }}>
                {(audit.estado === 'terminada' || audit.estado === 'entregada') && (
                  <>
                    <button
                      onClick={() => openHtmlReport(audit.id)}
                      style={{
                        background: '#3b82f6',
                        color: 'white',
                        border: 'none',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.75rem'
                      }}
                      title="Ver HTML"
                    >
                      🌐 HTML
                    </button>
                    <button
                      onClick={() => downloadPdfReport(audit.id)}
                      style={{
                        background: '#10b981',
                        color: 'white',
                        border: 'none',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.75rem'
                      }}
                      title="Descargar PDF"
                    >
                      📄 PDF
                    </button>
                    {audit.estado === 'entregada' && (
                      <button
                        onClick={() => devolverACorreccion(audit.id)}
                        style={{
                          background: '#f59e0b',
                          color: 'white',
                          border: 'none',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '0.75rem'
                        }}
                        title="Devolver para corrección"
                      >
                        ↩️ Corregir
                      </button>
                    )}
                  </>
                )}
              </div>

              <div>
                {['pendiente', 'asignada', 'en_proceso', 'pausada'].includes(audit.estado) && (
                  <button
                    onClick={() => handleReassignClick(audit)}
                    style={{
                      background: '#f59e0b',
                      color: 'white',
                      border: 'none',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.8rem'
                    }}
                    title="Reasignar a otro operador"
                  >
                    🔄
                  </button>
                )}
              </div>
              
              <div>
                <button
                  onClick={() => handleDeleteClick(audit)}
                  disabled={deleting === audit.id}
                  style={{
                    background: deleting === audit.id ? '#6c757d' : 
                               audit.antiguedad > 30 ? '#ffc107' : '#dc3545',
                    color: 'white',
                    border: 'none',
                    padding: '0.25rem 0.75rem',
                    borderRadius: '4px',
                    cursor: deleting === audit.id ? 'not-allowed' : 'pointer',
                    fontSize: '0.8rem',
                    opacity: deleting === audit.id ? 0.6 : 1
                  }}
                  title={audit.antiguedad > 30 ? 
                    'Eliminar (solo limpieza)' : 
                    'Eliminar (revierte pagos/cobros)'
                  }
                >
                  {deleting === audit.id ? '⏳' : 
                   audit.antiguedad > 30 ? '🗑️' : '❌'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal de confirmación */}
      {showConfirm && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            padding: '2rem',
            borderRadius: '8px',
            maxWidth: '500px',
            width: '90%'
          }}>
            <h3 style={{ margin: '0 0 1rem 0', color: '#495057' }}>
              {showConfirm.type === 'cleanup' ? '🗑️ Confirmar Limpieza' : '❌ Confirmar Corrección'}
            </h3>
            
            <div style={{ marginBottom: '1.5rem', lineHeight: '1.5' }}>
              {showConfirm.type === 'cleanup' ? (
                <div>
                  <p><strong>Tipo:</strong> Limpieza de auditoría antigua (+30 días)</p>
                  <p><strong>Efecto:</strong> Solo eliminará la auditoría para liberar espacio</p>
                  <p style={{ color: '#28a745' }}><strong>✅ NO afectará:</strong> Pagos a operadores ni cobros a clientes</p>
                </div>
              ) : (
                <div>
                  <p><strong>Tipo:</strong> Corrección de auditoría reciente (-30 días)</p>
                  <p style={{ color: '#dc3545' }}><strong>⚠️ SÍ afectará:</strong> Revertirá pagos y cobros</p>
                  <ul style={{ margin: '0.5rem 0', paddingLeft: '1.5rem' }}>
                    <li>Se descontará el pago al operador</li>
                    <li>Se eliminará el cobro al cliente</li>
                    <li>Se ajustarán los estados de cuenta</li>
                  </ul>
                </div>
              )}
              <p><strong>¿Estás seguro de continuar?</strong></p>
            </div>
            
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowConfirm(null)}
                style={{
                  background: '#6c757d',
                  color: 'white',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancelar
              </button>
              <button
                onClick={confirmDelete}
                style={{
                  background: showConfirm.type === 'cleanup' ? '#ffc107' : '#dc3545',
                  color: 'white',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                {showConfirm.type === 'cleanup' ? '🗑️ Limpiar' : '❌ Eliminar y Revertir'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de reasignación */}
      {showReassignModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            padding: '2rem',
            borderRadius: '8px',
            boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
            maxWidth: '500px',
            width: '90%'
          }}>
            <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.25rem' }}>
              🔄 Reasignar Auditoría #{showReassignModal.audit.id}
            </h3>
            
            <div style={{ marginBottom: '1rem', fontSize: '0.9rem' }}>
              <p><strong>Título:</strong> {showReassignModal.audit.titulo}</p>
              <p><strong>Cliente:</strong> {showReassignModal.audit.cliente}</p>
              <p><strong>Operador actual:</strong> {showReassignModal.audit.operador || 'Sin asignar'}</p>
            </div>
            
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                Nuevo Operador:
              </label>
              <select 
                value={selectedOperator} 
                onChange={(e) => setSelectedOperator(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #ced4da',
                  borderRadius: '4px',
                  fontSize: '0.9rem'
                }}
              >
                <option value="">Seleccionar operador...</option>
                {operadores.map(op => (
                  <option key={op.id} value={op.id.toString()}>
                    {op.name} ({op.username})
                  </option>
                ))}
              </select>
            </div>
            
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowReassignModal(null)}
                style={{
                  background: '#6c757d',
                  color: 'white',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancelar
              </button>
              <button
                onClick={handleReassignConfirm}
                disabled={!selectedOperator}
                style={{
                  background: selectedOperator ? '#f59e0b' : '#d1d5db',
                  color: 'white',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '4px',
                  cursor: selectedOperator ? 'pointer' : 'not-allowed'
                }}
              >
                🔄 Reasignar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditManagement;